import React, { Component } from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import FoundationLayout from '../components/layout/FoundationLayout';
import { getFoundationStudent } from '../foundationSession';
import './_foundationTests.scss';

/**
 * IIT Foundation test module — subject-wise, chapter-wise exams.
 *
 * Each chapter is an exam (its f_questions bank). Chapters are fetched for the
 * logged-in student's class (class_id = grade_id from the in-memory session,
 * no cookies/localStorage) and grouped by subject with a subject filter.
 */
const GET_FOUNDATION_CHAPTER_EXAMS = gql`
  query ($class_id: Int!, $subject: String) {
    getFoundationChapterExams(class_id: $class_id, subject: $subject) {
      chapter_id
      chapter_name
      chapter_number
      subject
      subject_id
      class_label
      total_questions
    }
  }
`;

// per-subject accent colour + icon, matched case-insensitively on the name
// (colours follow the foundation theme's subject palette, see theme/README.md)
const SUBJECT_STYLE = [
  { test: /phys/i, color: 'var(--fdn-violet)', icon: 'fa-atom' },
  { test: /chem/i, color: 'var(--fdn-teal)', icon: 'fa-flask' },
  { test: /bio/i, color: 'var(--fdn-green)', icon: 'fa-dna' },
  { test: /math/i, color: 'var(--fdn-orange)', icon: 'fa-square-root-alt' },
  { test: /reason|apti|mental/i, color: 'var(--fdn-yellow)', icon: 'fa-brain' },
  { test: /sci/i, color: 'var(--fdn-pink)', icon: 'fa-microscope' },
  { test: /eng|lang/i, color: 'var(--fdn-blue)', icon: 'fa-book-open' },
];
const styleFor = (subject) => {
  const s = SUBJECT_STYLE.find((x) => x.test.test(subject || ''));
  return s || { color: 'var(--fdn-muted)', icon: 'fa-layer-group' };
};
// theme subject class (fdn-subj--physics …) — sets --subj / --subj-soft for colouring
const subjClass = (subject) => `fdn-subj--${String(subject || '').toLowerCase().replace(/[^a-z]+/g, '-')}`;

class FoundationTestsView extends Component {
  state = { subject: 'All' };

  // open the take-test page (its own route) instead of a modal
  startTest = (ex) => {
    const qs = new URLSearchParams({
      chapter: String(ex.chapter_id),
      subject: ex.subject_id != null ? String(ex.subject_id) : '',
      name: ex.chapter_name || 'Test',
    }).toString();
    const url = `/iit-foundation/take-test?${qs}`;
    if (this.props.history) this.props.history.push(url);
    else window.location.assign(url);
  };

  render() {
    const student = getFoundationStudent() || {};
    const q = this.props.exams || {};
    const loading = q.loading;
    const all = q.getFoundationChapterExams || [];

    const subjects = [];
    all.forEach((e) => {
      if (e.subject && subjects.indexOf(e.subject) === -1) subjects.push(e.subject);
    });

    const active = this.state.subject;
    const visible = active === 'All' ? all : all.filter((e) => e.subject === active);

    // group visible chapters by subject, preserving order
    const groups = [];
    const index = {};
    visible.forEach((e) => {
      const key = e.subject || 'Other';
      if (index[key] === undefined) { index[key] = groups.length; groups.push({ subject: key, items: [] }); }
      groups[index[key]].items.push(e);
    });

    return (
      <FoundationLayout student={getFoundationStudent()} title="Tests">
        <div className="fte">
          <header className="fte-head fdn-hero fdn-hero--blue">
            <div className="fdn-hero__row">
              <span className="fdn-hero__icon"><i className="fas fa-clipboard-check" /></span>
              <div>
                <h2 className="fdn-hero__title">Chapter-wise Tests</h2>
                <p className="fdn-hero__sub">{student.class ? `${student.class} — ` : ''}Pick a subject and chapter to begin</p>
              </div>
              {!loading && all.length > 0 && (
                <div className="fdn-hero__stats">
                  <div className="fdn-hero__stat"><strong>{all.length}</strong><small>Chapters</small></div>
                  <div className="fdn-hero__stat"><strong>{subjects.length}</strong><small>Subjects</small></div>
                </div>
              )}
            </div>
          </header>

          <div className="fte-tabs">
            <button
              type="button"
              className={`fdn-chip ${active === 'All' ? 'is-active' : ''}`}
              onClick={() => this.setState({ subject: 'All' })}
            >
              <i className="fas fa-th-large" /> All
            </button>
            {subjects.map((s) => {
              const st = styleFor(s);
              return (
                <button
                  key={s}
                  type="button"
                  className={`fdn-chip ${subjClass(s)} ${active === s ? 'is-active' : ''}`}
                  onClick={() => this.setState({ subject: s })}
                >
                  <i className={`fas ${st.icon}`} /> {s}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="fte-loading">
              <div className="fte-grid" aria-hidden="true">
                {[0, 1, 2, 3].map((n) => (
                  <div className="fte-card fte-card--skel fdn-card" key={n}>
                    <span className="fte-skel-icon" />
                    <span className="fdn-skel" style={{ width: '45%' }} />
                    <span className="fdn-skel" style={{ width: '85%', height: 16 }} />
                    <span className="fdn-skel fte-skel-btn" />
                  </div>
                ))}
              </div>
              <p className="fte-loading-text"><i className="fas fa-circle-notch fa-spin" /> Loading tests…</p>
            </div>
          ) : !all.length ? (
            <div className="fte-empty fdn-card fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-clipboard-list" /></div>
              <h4>Nothing here yet 📭</h4>
              <p>No tests available for your class yet.</p>
            </div>
          ) : (
            groups.map((group) => {
              const st = styleFor(group.subject);
              return (
                <section className={`fte-group ${subjClass(group.subject)}`} key={group.subject}>
                  <h3 className="fte-group-title">
                    <span className="fte-group-icon"><i className={`fas ${st.icon}`} /></span> {group.subject}
                    <span className="fte-group-count">{group.items.length}</span>
                  </h3>
                  <div className="fte-grid">
                    {group.items.map((ex) => {
                      const mins = Math.max(1, Math.round((ex.total_questions || 0)));
                      return (
                        <div className="fte-card fdn-card fdn-card--hover fdn-pop" key={ex.chapter_id}>
                          <div className="fte-card-top">
                            <span className="fte-card-icon fdn-card__icon">
                              <i className={`fas ${st.icon}`} />
                            </span>
                            <span className="fte-card-num">Chapter {ex.chapter_number}</span>
                          </div>
                          <div className="fte-card-name">{ex.chapter_name}</div>
                          {ex.total_questions > 0 && (
                            <div className="fte-card-meta">
                              <span className="fdn-badge"><i className="far fa-clock" /> ~{mins} min</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className="fte-card-btn fdn-btn fdn-btn--subject fdn-btn--block"
                            disabled={!ex.total_questions}
                            onClick={() => this.startTest(ex)}
                          >
                            {ex.total_questions ? (
                              <>Start Test <i className="fas fa-arrow-right" /></>
                            ) : 'No questions yet'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </FoundationLayout>
    );
  }
}

const FoundationTests = graphql(GET_FOUNDATION_CHAPTER_EXAMS, {
  name: 'exams',
  skip: () => {
    const s = getFoundationStudent();
    return !(s && s.grade_id);
  },
  options: () => {
    const s = getFoundationStudent();
    return {
      variables: { class_id: Number(s.grade_id), subject: null },
      fetchPolicy: 'cache-and-network',
    };
  },
})(FoundationTestsView);

/** Renders a prompt when there's no session; otherwise the class's tests. */
class IITFoundationTests extends Component {
  render() {
    const s = getFoundationStudent();
    if (!s || !s.grade_id) {
      return (
        <FoundationLayout student={s}>
          <div className="fte">
            <div className="fte-empty fdn-card fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-user-lock" /></div>
              <h4>Hold on! 🔐</h4>
              <p>Please log in to see your class tests.</p>
            </div>
          </div>
        </FoundationLayout>
      );
    }
    return <FoundationTests {...this.props} />;
  }
}

export default IITFoundationTests;
